/**
 * The disk guard, in one place for the node that enforces it and the screens that show it.
 *
 * The owner rule itself lives in root AGENTS.md: Brunel cleans up everything it creates on a
 * computer, and it never runs out of disk silently. This is only the arithmetic. The database
 * applies the same two numbers in `brunel_private.disk_low` and spells them out again in
 * `brunel.node_pressure`; a change here is a change there.
 *
 * A reading is of ONE volume: the one that holds the run checkouts. A computer that cannot
 * say sends no reading, and no reading holds nothing back.
 */

const GIBIBYTE = 1024 * 1024 * 1024;

/** With less free than this, a computer takes no new work. */
export const DISK_MIN_FREE_BYTES = 15 * GIBIBYTE;

/** With a smaller share free than this, the same. Either one is enough. */
export const DISK_MIN_FREE_PERCENT = 5;

export interface DiskReading {
  readonly freeBytes: number;
  readonly totalBytes: number;
}

export type DiskLowReason = 'free_bytes' | 'free_percent';

function usable(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

/** Free share of the volume, to one decimal. Null when the size of the volume is not known. */
export function diskFreePercent(reading: DiskReading): number | null {
  if (!usable(reading.freeBytes) || !usable(reading.totalBytes) || reading.totalBytes === 0) return null;
  return Math.round(Math.min(1, reading.freeBytes / reading.totalBytes) * 1000) / 10;
}

/**
 * Which threshold a reading is under. The share is compared exactly rather than through the
 * rounded figure shown to the owner, so 4.96% is low even though it reads as 5.0%.
 */
export function diskLowReasons(reading: DiskReading): DiskLowReason[] {
  if (!usable(reading.freeBytes)) return [];
  const reasons: DiskLowReason[] = [];
  if (reading.freeBytes < DISK_MIN_FREE_BYTES) reasons.push('free_bytes');
  if (usable(reading.totalBytes) && reading.totalBytes > 0
    && reading.freeBytes * 100 < reading.totalBytes * DISK_MIN_FREE_PERCENT) reasons.push('free_percent');
  return reasons;
}

export function diskIsLow(reading: DiskReading): boolean {
  return diskLowReasons(reading).length > 0;
}

/** Free space still needed before both thresholds are met. Zero when they already are. */
export function diskBytesShort(reading: DiskReading): number {
  if (!usable(reading.freeBytes)) return 0;
  const byShare = usable(reading.totalBytes) ? Math.ceil(reading.totalBytes * DISK_MIN_FREE_PERCENT / 100) : 0;
  return Math.max(0, Math.max(DISK_MIN_FREE_BYTES, byShare) - reading.freeBytes);
}

/** A size as the operating system shows it: binary units, one decimal above a gigabyte. */
export function formatDiskBytes(bytes: number): string {
  if (!usable(bytes)) return 'Unknown';
  if (bytes >= GIBIBYTE) return `${(bytes / GIBIBYTE).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round(bytes)} B`;
}

/** "31.4 GB free of 930.5 GB (3.4%)", or just the free figure when the volume size is unknown. */
export function diskFreeLabel(reading: DiskReading): string {
  const percent = diskFreePercent(reading);
  return percent === null
    ? `${formatDiskBytes(reading.freeBytes)} free`
    : `${formatDiskBytes(reading.freeBytes)} free of ${formatDiskBytes(reading.totalBytes)} (${percent.toFixed(1)}%)`;
}
